import React from 'react';
import growsfLogo from '../../images/logo-clean.svg';

const CrossTabChart = ({
  data,
  infoList,
  chartInfo = true,
  fullSpace = true,
  direction = 'horizontal',
  bottomInfo = true,
  totalN = 1000, // Total sample size for MOE calculation
}) => {
  // Calculate MOE for 95% confidence interval
  const calculateMOE = (sampleSize) => {
    if (!sampleSize || sampleSize <= 0) return null;
    const moe = 1.96 * Math.sqrt((0.5 * 0.5) / sampleSize) * 100;
    return Math.round(moe * 10) / 10; // Round to 1 decimal place
  };

  // Helper function to get support percentage
  const getSupportPercentage = (item) => {
    if (item.totalSupport !== undefined) {
      return item.totalSupport;
    }
    if (item.stackedList.length === 4) {
      return parseInt(item.stackedList[0].value) + parseInt(item.stackedList[1].value);
    }
    if (item.stackedList.length === 2) {
      return parseInt(item.stackedList[0].value);
    }
    return 0;
  };

  // Helper function to get oppose percentage
  const getOpposePercentage = (item) => {
    if (item.totalOppose !== undefined) {
      return item.totalOppose;
    }
    if (item.stackedList.length === 4) {
      return parseInt(item.stackedList[2].value) + parseInt(item.stackedList[3].value);
    }
    if (item.stackedList.length === 2) {
      return parseInt(item.stackedList[1].value);
    }
    return 0;
  };

  return (
    <div className="space-y-4">
      {data.map((category, categoryIndex) => (
        <div key={categoryIndex} className="space-y-2">
          {category.categoryName && (
            <div className="text-xs font-medium text-gray-600 uppercase tracking-wide border-b border-gray-200 pb-0.5 mb-1 flex items-center gap-2">
              <span className="flex-1">{category.categoryName}</span>
              <img src={growsfLogo} alt="GrowSF" className="h-3 !m-0 w-auto opacity-80" />
            </div>
          )}

          {/* Column headers - only show for first category */}
          {categoryIndex === 0 && (
            <div className="hidden sm:flex items-center gap-3 text-xs text-gray-500 font-medium">
              <div className="w-24 sm:w-32 text-right flex-shrink-0" />
              <div className="flex-1" />
              <div className="flex w-12 sm:w-16 flex-shrink-0 items-end justify-between px-1">
                <span className="text-green-600 transform -rotate-45 origin-bottom text-xs">
                  Support
                </span>
                <span className="text-red-600 transform -rotate-45 origin-bottom text-xs">
                  Oppose
                </span>
              </div>
              <div className="w-6 sm:w-8 text-center">N</div>
              <div className="w-8 sm:w-10 text-center">MOE</div>
            </div>
          )}

          <div className="space-y-1 sm:space-y-1">
            {category.items.map((item, itemIndex) => {
              const sampleSize = item.n || null;
              const moe = sampleSize ? calculateMOE(sampleSize) : null;
              const supportPercentage = getSupportPercentage(item);
              const opposePercentage = getOpposePercentage(item);

              return (
                <div key={itemIndex}>
                  {/* Mobile layout */}
                  <div className="block sm:hidden p-1 bg-gray-50 rounded">
                    <div className="text-sm font-medium text-new-dark-75">{item.title}</div>
                    <div className="flex h-6 rounded-sm overflow-hidden">
                      {item.stackedList.map((segment, segmentIndex) => (
                        <div
                          key={segmentIndex}
                          className="flex items-center justify-center text-xs font-medium text-black"
                          style={{
                            width: `${segment.value}%`,
                            background: segment.bgColor,
                            minWidth: parseInt(segment.value) > 5 ? 'auto' : '0',
                          }}
                        >
                          {parseInt(segment.value) > 4 && `${segment.value}%`}
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between text-sm">
                      <div className="flex gap-4">
                        <span className="text-green-600 font-medium">
                          Support: {supportPercentage}%
                        </span>
                        <span className="text-red-600 font-medium">
                          Oppose: {opposePercentage}%
                        </span>
                      </div>
                      <div className="text-gray-600 text-xs">
                        N={sampleSize || '-'} MOE={moe ? `±${moe}%` : '-'}
                      </div>
                    </div>
                  </div>
                  {/* Desktop layout */}
                  <div className="hidden sm:flex items-center gap-3">
                    {/* Label on the left */}
                    <div className="w-24 sm:w-32 text-sm text-new-dark-75 text-right flex-shrink-0">
                      {item.title}
                    </div>

                    {/* Compact bar */}
                    <div className="flex-1">
                      <div className="flex h-6 rounded-sm overflow-hidden">
                        {item.stackedList.map((segment, segmentIndex) => (
                          <div
                            key={segmentIndex}
                            className="flex items-center justify-center text-xs font-medium text-black"
                            style={{
                              width: `${segment.value}%`,
                              background: segment.bgColor,
                              minWidth: parseInt(segment.value) > 5 ? 'auto' : '0',
                            }}
                          >
                            {parseInt(segment.value) > 4 && `${segment.value}%`}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Total support/oppose percentages */}
                    <div className="flex gap-1 text-sm font-medium w-12 sm:w-16 flex-shrink-0">
                      {item.stackedList.length === 4 && (
                        <>
                          <span className="text-green-600">{supportPercentage}%</span>
                          <span className="text-red-600">{opposePercentage}%</span>
                        </>
                      )}
                      {item.stackedList.length === 2 && (
                        <>
                          <span className="text-green-600">{supportPercentage}%</span>
                          <span className="text-red-600">{opposePercentage}%</span>
                        </>
                      )}
                    </div>

                    {/* Sample size */}
                    <div className="w-6 sm:w-8 text-center text-sm text-gray-600">
                      {sampleSize || '-'}
                    </div>

                    {/* Margin of Error */}
                    <div className="w-8 sm:w-10 text-center text-sm text-gray-600">
                      {moe ? `±${moe}%` : '-'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {/* Legend - only show once at the bottom */}
      {chartInfo && infoList && (
        <div className="flex flex-wrap gap-4 text-xs">
          {infoList.map((info, index) => (
            <div key={index} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm" style={{ background: info.bgColorInfo }} />
              <span className="text-gray-600">{info.textInfo}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CrossTabChart;
